import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Shield, Package, CheckCircle, Heart, ArrowRight, Droplets, FlaskConical, Droplet, Sun as SunIcon, TrendingDown, TrendingUp, AlertCircle } from "lucide-react";

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/c800bfeb-ea68-478b-a760-5babe8c059d0/images/dd6a16e59a2efe4ccb4ee4ab6e5bfad46c22222552944cce07d70c10cd9a58e9.png";

const CONCERNS = [
  { id: "breakouts", label: "Breakouts" },
  { id: "oily", label: "Oily skin" },
  { id: "sensitive", label: "Sensitive" },
  { id: "dry", label: "Dry skin" },
  { id: "combination", label: "Combination" },
];

const ROUTINES = {
  breakouts: {
    name: "Priya's Routine",
    avatar: "https://static.prod-images.emergentagent.com/jobs/b9fd19fa-914b-40ea-b71c-333af0dc61ff/images/2759fb2e7d60a07398dc1511ee2c379401642a08bb2d85fd7e06150f8a1cf0a5.png",
    tag: "Oily Acne-Prone Skin",
    cardHeader: "Routine That Helped Reduce Breakouts",
    steps: [
      { step: "Cleanse", product: "Minimalist Salicylic Cleanser", icon: Droplets, color: "#3B82F6", img: "https://m.media-amazon.com/images/I/514PVblwPdL.jpg" },
      { step: "Treat", product: "Derma Co Salicylic Acid Serum", icon: FlaskConical, color: "#8B5CF6", img: "https://m.media-amazon.com/images/I/51HRdABtKQL._SL1080_.jpg" },
      { step: "Hydrate", product: "Sebamed Clear Face Gel", icon: Droplet, color: "#06B6D4", img: "https://m.media-amazon.com/images/I/51qckvUnZDL.jpg" },
      { step: "Protect", product: "Re'equil Ultra Matte Sunscreen", icon: SunIcon, color: "#F59E0B", img: "https://m.media-amazon.com/images/I/41suy3Vsh1L._AC_UF1000,1000_QL80_.jpg" },
    ],
    results: [
      { label: "Breakouts", value: "↓ 64%", type: "down" },
      { label: "Oil Control", value: "↑ 71%", type: "up" },
      { label: "Dryness", value: "18%", type: "warn" },
    ],
  },
  oily: {
    name: "Kavya's Routine",
    avatar: "https://static.prod-images.emergentagent.com/jobs/7ee131c6-4bab-4dd1-83de-596734e4340a/images/9f1cb1205fedb9458d792d28c3220a32fc01fd2cc36095986f4f88a023ecaba7.png",
    tag: "Oily T-Zone Skin",
    cardHeader: "Routine That Helped Control Oiliness",
    steps: [
      { step: "Cleanse", product: "COSRX Salicylic Acid Cleanser", icon: Droplets, color: "#3B82F6", img: "https://m.media-amazon.com/images/I/51k2Qkd-OeL._AC_UF1000,1000_QL80_.jpg" },
      { step: "Treat", product: "Minimalist 10% Niacinamide", icon: FlaskConical, color: "#8B5CF6", img: "https://m.media-amazon.com/images/I/51fo5Cdx4YL.jpg" },
      { step: "Hydrate", product: "Plum Green Tea Moisturizer", icon: Droplet, color: "#06B6D4", img: "https://m.media-amazon.com/images/I/61v1amrV+PL.jpg" },
      { step: "Protect", product: "Minimalist SPF 50 Sunscreen", icon: SunIcon, color: "#F59E0B", img: "https://m.media-amazon.com/images/I/51liYV8g2DL.jpg" },
    ],
    results: [
      { label: "Oiliness", value: "↓ 72%", type: "down" },
      { label: "Pore Size", value: "↓ 58%", type: "down" },
      { label: "Dryness", value: "12%", type: "warn" },
    ],
  },
  sensitive: {
    name: "Meera's Routine",
    avatar: "https://static.prod-images.emergentagent.com/jobs/7ee131c6-4bab-4dd1-83de-596734e4340a/images/8c97fa7f0c840a5a1ca41668c428c42906a9e9b3a505ab957a5311810b2356b3.png",
    tag: "Sensitive Redness-Prone Skin",
    cardHeader: "Routine That Helped Calm Sensitivity",
    steps: [
      { step: "Cleanse", product: "Simple Refreshing Face Wash", icon: Droplets, color: "#3B82F6", img: "https://m.media-amazon.com/images/I/51EFM8yM+ZL.jpg" },
      { step: "Treat", product: "The Ordinary Niacinamide + Zinc", icon: FlaskConical, color: "#8B5CF6", img: "https://m.media-amazon.com/images/I/612NxRjAoCL._AC_UF1000,1000_QL80_.jpg" },
      { step: "Hydrate", product: "Cetaphil Moisturizing Lotion", icon: Droplet, color: "#06B6D4", img: "https://m.media-amazon.com/images/I/518jEfll4xL._AC_UF1000,1000_QL80_.jpg" },
      { step: "Protect", product: "La Roche-Posay Anthelios SPF 50", icon: SunIcon, color: "#F59E0B", img: "https://m.media-amazon.com/images/I/71rhJ0EUn1L._AC_UF1000,1000_QL80_.jpg" },
    ],
    results: [
      { label: "Irritation", value: "↓ 68%", type: "down" },
      { label: "Redness", value: "↓ 55%", type: "down" },
      { label: "Dryness", value: "22%", type: "warn" },
    ],
  },
  dry: {
    name: "Arjun's Routine",
    avatar: "https://static.prod-images.emergentagent.com/jobs/7ee131c6-4bab-4dd1-83de-596734e4340a/images/8e8650a0cc577d4810d8dfdd8069fd4a25b43b3efc9bc0f2dc3d4f682ad5a280.png",
    tag: "Dry Dehydrated Skin",
    cardHeader: "Routine That Helped Boost Hydration",
    steps: [
      { step: "Cleanse", product: "Cetaphil Gentle Cleanser", icon: Droplets, color: "#3B82F6", img: "https://m.media-amazon.com/images/I/61uaWLDEqLL._AC_UF1000,1000_QL80_.jpg" },
      { step: "Treat", product: "Dot & Key Cica Niacinamide Serum", icon: FlaskConical, color: "#8B5CF6", img: "https://m.media-amazon.com/images/I/51EpZT48XJL.jpg" },
      { step: "Hydrate", product: "Neutrogena Hydro Boost Gel", icon: Droplet, color: "#06B6D4", img: "https://m.media-amazon.com/images/I/71Wec9b3eXL.jpg" },
      { step: "Protect", product: "COSRX Aloe Sun Cream SPF 50", icon: SunIcon, color: "#F59E0B", img: "https://m.media-amazon.com/images/I/71ErYCZuNUL._AC_UF350,350_QL80_.jpg" },
    ],
    results: [
      { label: "Hydration", value: "↑ 78%", type: "up" },
      { label: "Flaking", value: "↓ 65%", type: "down" },
      { label: "Dryness", value: "↓ 71%", type: "down" },
    ],
  },
  combination: {
    name: "Ananya's Routine",
    avatar: "https://static.prod-images.emergentagent.com/jobs/7ee131c6-4bab-4dd1-83de-596734e4340a/images/376cd07fcd01f09d520486e1b78a700c4eaa1a03dd8451103b7ca8ebd085edb2.png",
    tag: "Combination Uneven Skin",
    cardHeader: "Routine That Helped Balance Skin",
    steps: [
      { step: "Cleanse", product: "Dot & Key Cica Face Wash", icon: Droplets, color: "#3B82F6", img: "https://m.media-amazon.com/images/I/614cf2b7N-L._AC_UF1000,1000_QL80_.jpg" },
      { step: "Treat", product: "Re'equil Pore Refining Serum", icon: FlaskConical, color: "#8B5CF6", img: "https://m.media-amazon.com/images/I/61Fko227APL.jpg" },
      { step: "Hydrate", product: "Dot & Key Cica Moisturizer", icon: Droplet, color: "#06B6D4", img: "https://m.media-amazon.com/images/I/61+zM7LCbrL._AC_UF1000,1000_QL80_.jpg" },
      { step: "Protect", product: "Aqualogica Dewy Sunscreen SPF 50", icon: SunIcon, color: "#F59E0B", img: "https://m.media-amazon.com/images/I/61ACtF1fouL._AC_UF1000,1000_QL80_.jpg" },
    ],
    results: [
      { label: "Balance", value: "↑ 62%", type: "up" },
      { label: "Texture", value: "↑ 70%", type: "up" },
      { label: "Dryness", value: "15%", type: "warn" },
    ],
  },
};

const RoutinePreviewCard = ({ routine }) => {
  if (!routine) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xl shadow-slate-200/50 overflow-hidden w-full max-w-[450px] h-[430px] flex flex-col" style={{ animation: 'cardFadeIn 0.6s ease-out' }} data-testid="hero-routine-card">
    {/* Header */}
    <div className="px-5 pt-4 pb-2.5 border-b border-slate-100">
      <p className="text-[10px] font-bold uppercase tracking-widest text-[#4A55A6] mb-2">{routine.cardHeader}</p>
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 border border-slate-200">
          <img src={routine.avatar} alt={routine.name} className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-xs font-semibold text-foreground leading-none">{routine.name}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{routine.tag}</p>
        </div>
      </div>
    </div>

    {/* Steps */}
    <div className="px-5 py-2 flex-1">
      {routine.steps.map(({ step, product, icon: Icon, color, img }, i) => (
        <div key={step} className={`flex items-center gap-3 py-2 ${i < routine.steps.length - 1 ? "border-b border-slate-50" : ""}`}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: color + "12" }}>
            <Icon className="w-3.5 h-3.5" style={{ color }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 leading-none mb-0.5">{step}</p>
            <p className="text-xs font-medium text-foreground leading-snug truncate">{product}</p>
          </div>
          <div className="w-8 h-8 rounded-lg border border-slate-100 overflow-hidden bg-slate-50 shrink-0">
            <img src={img} alt={product} className="w-full h-full object-cover" />
          </div>
        </div>
      ))}
    </div>

    {/* Community Results */}
    <div className="mx-5 mb-4 rounded-xl bg-slate-50 border border-slate-100 px-4 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2.5">Community Results</p>
      <div className="flex items-center gap-5">
        {routine.results.map(({ label, value, type }) => (
          <div key={label} className="flex items-center gap-1.5">
            {type === "warn" ? (
              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
            ) : type === "down" ? (
              <TrendingDown className="w-3.5 h-3.5 text-emerald-500" />
            ) : (
              <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
            )}
            <span className={`text-xs font-semibold ${type === "warn" ? "text-amber-600" : "text-emerald-700"}`}>{label} <span className={type === "warn" ? "text-amber-500" : "text-emerald-600"}>{value}</span></span>
          </div>
        ))}
      </div>
    </div>
    </div>
  );
};

export const HeroSection = ({ onExplore }) => {
  const [selectedConcern, setSelectedConcern] = useState("breakouts");
  const userClickedRef = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Wait 5s, then auto-rotate every 4s — stop if user clicks a pill
    const ids = CONCERNS.map(c => c.id);
    const startRotation = () => {
      intervalRef.current = setInterval(() => {
        if (userClickedRef.current) return;
        setSelectedConcern(prev => {
          const idx = ids.indexOf(prev);
          return ids[(idx + 1) % ids.length];
        });
      }, 4000);
    };
    const delay = setTimeout(startRotation, 5000);
    return () => {
      clearTimeout(delay);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleSelect = useCallback((id) => {
    userClickedRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setSelectedConcern(id);
  }, []);

  const routine = ROUTINES[selectedConcern] || ROUTINES["breakouts"];

  return (
    <section className="hero-gradient pt-28 pb-16 px-4" data-testid="hero-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center">
          {/* Left */}
          <div className="pt-4">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white/70 backdrop-blur-sm rounded-full border border-slate-200/60 animate-fade-in">
              <Shield className="w-3.5 h-3.5 text-[#5B6BA8]" />
              <span className="text-xs font-semibold tracking-wide text-[#5B6BA8] uppercase">Community-Backed Skincare</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-foreground leading-[1.08] tracking-tight mb-6 animate-fade-in" data-testid="hero-heading">
              Clearer Skin Starts With{" "}
              <span className="text-gradient-brand">the Right Routine.</span>
            </h1>

            <div className="mb-8 animate-fade-in opacity-0 stagger-1" style={{ animationFillMode: "forwards" }}>
              <p className="text-base sm:text-lg text-foreground/70 leading-relaxed max-w-xl">
                Discover routines and products shared by real people — validated by the community, matched to your skin.
              </p>
            </div>

            {/* Concern pills */}
            <div className="mb-8 animate-fade-in opacity-0 stagger-2" style={{ animationFillMode: "forwards" }}>
              <p className="text-sm font-semibold text-[#4A55A6] mb-3">What's your main skin concern?</p>
              <div className="flex flex-wrap gap-2">
                {CONCERNS.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => handleSelect(id)}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 hover:-translate-y-0.5 ${
                      selectedConcern === id
                        ? "bg-white border-2 border-[#5B6BA8] text-[#5B6BA8] shadow-md shadow-[#5B6BA8]/15"
                        : "bg-white border border-slate-200 text-foreground/70 hover:border-[#5B6BA8]/40 hover:text-[#5B6BA8] hover:shadow-sm"
                    }`}
                    data-testid={`concern-pill-${id}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start gap-4 animate-fade-in opacity-0 stagger-3" style={{ animationFillMode: "forwards" }}>
              <Link to="/routines">
                <Button size="lg" className="rounded-full px-10 h-12 text-base font-medium shadow-lg shadow-primary/20 hover:shadow-xl transition-all" data-testid="find-routine-btn">
                  Find My Routine
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" size="lg" className="rounded-full px-10 h-12 text-base font-medium border-2 border-[#4A55A6] text-[#4A55A6] hover:bg-[#4A55A6]/5" data-testid="explore-products-btn">
                  Explore Products
                </Button>
              </Link>
            </div>
          </div>

          {/* Right — Girl Image + Routine Card (desktop) */}
          <div className="hidden lg:flex justify-end items-start pt-2">
            <div className="relative w-full max-w-[640px]" data-testid="hero-visual">
              {/* Girl image */}
              <div
                className="absolute top-0 right-0 w-[320px] h-[460px] rounded-3xl overflow-hidden shadow-xl shadow-[#5B6BA8]/10 opacity-0 hero-image-enter"
                style={{ animationDelay: "0.3s" }}
              >
                <div className="absolute -inset-4 rounded-[2rem] bg-[#5B6BA8]/[0.04] blur-xl" />
                <img
                  src={HERO_IMAGE}
                  alt="Indian woman with clear glowing skin"
                  className="relative w-full h-full object-cover object-top"
                  data-testid="hero-image"
                />
              </div>
              {/* Routine card */}
              <div
                className="relative z-10 mt-5 mr-[150px] opacity-0 hero-card-enter"
                style={{ animationDelay: "0.7s" }}
              >
                <RoutinePreviewCard key={selectedConcern} routine={routine} />
              </div>
            </div>
          </div>
        </div>

        {/* Routine Card (mobile) */}
        <div className="lg:hidden flex justify-center mt-10 animate-fade-in opacity-0 stagger-3" style={{ animationFillMode: "forwards" }}>
          <RoutinePreviewCard key={`mobile-${selectedConcern}`} routine={routine} />
        </div>
      </div>
    </section>
  );
};
